/**
 * AuditEntryItem — single timeline entry with icon, timestamp, title,
 * expandable details, and slide-in animation.
 */

import { memo, useState, useEffect } from 'react';
import {
  Wrench,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  User,
  Bot,
  Loader2,
  Play,
  Brain,
  Sparkles,
  GitBranch,
  ArrowLeftRight,
  ExternalLink,
  ShieldAlert,
  CircleStop,
} from 'lucide-react';
import { cn } from '../../../utils';
import { Text } from '../../atoms/Typography';
import { Badge } from '../../atoms/Badge';
import { Button } from '../../atoms/Button';
import { ContentBlock } from './ContentBlock';
import { RunOutput, type KeptOutputLabelKey, type OpenKeptOutput } from './RunOutput';
import {
  BLOCKED_LABEL_DEFAULTS,
  blockedReasonText,
  forcedEndingNote,
  formatDuration,
  formatHITLDuration,
  formatTime,
  formatTokens,
  isStoppedNode,
  resolveDelegationState,
} from './utils';
import { searchConfidenceState, searchDegradedReason } from './searchConfidence';

// ============================================================================
// Smart tool output formatter — renders known output shapes as human-readable
// ============================================================================

interface SearchHit {
  title?: string;
  snippet?: string;
  url?: string;
  score?: number;
}

/**
 * Attempt to extract a human-readable summary from a tool output object.
 * Returns a React node if the output matches a known pattern, null otherwise.
 */
function formatToolOutput(output: unknown): { summary: string; hits?: SearchHit[]; isError?: boolean } | null {
  if (output == null || typeof output !== 'object') return null;
  const obj = output as Record<string, unknown>;

  // Error pattern: { error: { error_message: "...", http_status: 401 } }
  const errorObj = obj['error'] as Record<string, unknown> | string | undefined;
  if (errorObj) {
    const msg = typeof errorObj === 'string'
      ? errorObj
      : (errorObj as Record<string, unknown>)['error_message'] as string
        ?? (errorObj as Record<string, unknown>)['message'] as string
        ?? 'Error';
    return { summary: msg, isError: true };
  }

  // Search result pattern
  const hits = (obj['hits'] ?? obj['results'] ?? (obj['provider_raw_response'] as Record<string, unknown>)?.['results']) as SearchHit[] | undefined;
  const message = (obj['message'] as string) ?? undefined;
  const query = (obj['query'] as string)
    ?? ((obj['provider_response'] as Record<string, unknown>)?.['query'] as string)
    ?? ((obj['provider_raw_response'] as Record<string, unknown>)?.['query'] as string)
    ?? undefined;
  const provider = (obj['provider'] as string)
    ?? ((obj['provider_response'] as Record<string, unknown>)?.['provider'] as string)
    ?? ((obj['provider_raw_response'] as Record<string, unknown>)?.['provider'] as string)
    ?? undefined;

  if (hits !== undefined || message || query) {
    const hitList = Array.isArray(hits) ? hits : [];
    let summary = '';
    if (query) summary += `Search: "${query}"`;
    if (provider) summary += summary ? ` (${provider})` : provider;
    if (message) summary += summary ? ` — ${message}` : message;
    if (!summary && hitList.length > 0) summary = `${hitList.length} result${hitList.length === 1 ? '' : 's'}`;
    if (!summary) summary = 'Search completed';
    return { summary, hits: hitList.length > 0 ? hitList : undefined };
  }

  // Simple string result
  if (typeof obj['result'] === 'string') return { summary: obj['result'] };
  if (typeof obj['content'] === 'string') return { summary: obj['content'] };
  if (typeof obj['text'] === 'string') return { summary: obj['text'] };
  if (typeof obj['answer'] === 'string') return { summary: obj['answer'] };

  return null;
}

/** Render search hits as a compact list */
function SearchHitsRenderer({ hits }: { hits: SearchHit[] }) {
  return (
    <div className="space-y-1.5 mt-1.5">
      {hits.slice(0, 5).map((hit, i) => (
        <div key={i} className="rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-2">
          {hit.title && (
            <div className="flex items-center gap-1.5">
              <Text size="xs" weight="medium" className="truncate">{hit.title}</Text>
              {hit.url && (
                <a href={hit.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-blue-500 hover:text-blue-600" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
          {hit.snippet && (
            <Text size="xs" color="muted" className="line-clamp-2 mt-0.5">{hit.snippet}</Text>
          )}
        </div>
      ))}
      {hits.length > 5 && (
        <Text size="xs" color="muted">+{hits.length - 5} more</Text>
      )}
    </div>
  );
}
import type {
  AuditEntry,
  TurnAuditEntry,
  ToolCallAuditEntry,
  ToolResultAuditEntry,
  HITLRequestAuditEntry,
  HITLResponseAuditEntry,
  NodeStartAuditEntry,
  NodeCompleteAuditEntry,
  ErrorAuditEntry,
  OrchestratorDecisionAuditEntry,
  AgentThinkingAuditEntry,
  DelegationAuditEntry,
  DelegationResultAuditEntry,
  BlockedLabelKey,
  BlockedLabels,
  DelegationResultState,
  ForcedLabelKey,
} from './types';

/** An error entry marked as a warning: an ending to check, not a crash. */
function isWarningEntry(entry: AuditEntry): boolean {
  return entry.type === 'error' && (entry as ErrorAuditEntry).severity === 'warning';
}

/** Amber marks a request the mission setup refused. */
const BLOCKED_ICON_CLASS = 'text-amber-500/60 dark:text-amber-400/50';

/** Get icon config for an audit entry type */
function getEntryIcon(entry: AuditEntry): { icon: typeof MessageSquare; className: string } {
  switch (entry.type) {
    case 'turn':
      return { icon: MessageSquare, className: 'text-blue-500/60 dark:text-blue-400/50' };
    case 'tool_call': {
      const call = entry as ToolCallAuditEntry;
      if (call.resolved) {
        return call.resolvedSuccess
          ? { icon: CheckCircle2, className: 'text-emerald-500/60 dark:text-emerald-400/50' }
          : { icon: XCircle, className: 'text-rose-500/60 dark:text-rose-400/50' };
      }
      return { icon: Wrench, className: 'text-purple-500/60 dark:text-purple-400/50' };
    }
    case 'tool_result':
      return (entry as ToolResultAuditEntry).success
        ? { icon: CheckCircle2, className: 'text-emerald-500/60 dark:text-emerald-400/50' }
        : { icon: XCircle, className: 'text-rose-500/60 dark:text-rose-400/50' };
    case 'hitl_request':
      return (entry as HITLRequestAuditEntry).pending
        ? { icon: Loader2, className: 'text-amber-500/60 dark:text-amber-400/50 animate-spin' }
        : { icon: User, className: 'text-amber-500/60 dark:text-amber-400/50' };
    case 'hitl_response':
      return { icon: User, className: 'text-emerald-500/60 dark:text-emerald-400/50' };
    case 'node_start':
      return { icon: Play, className: 'text-blue-500/60 dark:text-blue-400/50' };
    case 'node_complete': {
      const complete = entry as NodeCompleteAuditEntry;
      // A run a person stopped did not fail: a neutral mark (agion/Agion#2947).
      if (isStoppedNode(complete)) return { icon: CircleStop, className: 'text-slate-500/60 dark:text-slate-400/50' };
      const state = resolveDelegationState(complete.success, complete.outcome);
      if (state === 'blocked') return { icon: ShieldAlert, className: BLOCKED_ICON_CLASS };
      if (forcedEndingNote(complete.success, complete.forcedReason)) {
        return { icon: AlertTriangle, className: 'text-amber-500/60 dark:text-amber-400/50' };
      }
      return state === 'succeeded'
        ? { icon: CheckCircle2, className: 'text-emerald-500/60 dark:text-emerald-400/50' }
        : { icon: XCircle, className: 'text-rose-500/60 dark:text-rose-400/50' };
    }
    case 'error':
      return isWarningEntry(entry)
        ? { icon: AlertTriangle, className: 'text-amber-500/60 dark:text-amber-400/50' }
        : { icon: AlertCircle, className: 'text-rose-500/60 dark:text-rose-400/50' };
    case 'orchestrator_decision':
      return { icon: Brain, className: 'text-fuchsia-500/60 dark:text-fuchsia-400/50' };
    case 'agent_thinking':
      return { icon: Sparkles, className: 'text-violet-500/60 dark:text-violet-400/50' };
    case 'delegation': {
      const del = entry as DelegationAuditEntry;
      if (del.resolved) {
        const state = resolveDelegationState(del.resolvedSuccess, del.resolvedOutcome);
        if (state === 'blocked') return { icon: ShieldAlert, className: BLOCKED_ICON_CLASS };
        return state === 'succeeded'
          ? { icon: CheckCircle2, className: 'text-emerald-500/60 dark:text-emerald-400/50' }
          : { icon: XCircle, className: 'text-rose-500/60 dark:text-rose-400/50' };
      }
      return { icon: GitBranch, className: 'text-violet-500/60 dark:text-violet-400/50' };
    }
    case 'delegation_result': {
      const delResult = entry as DelegationResultAuditEntry;
      const state = resolveDelegationState(delResult.success, delResult.outcome);
      if (state === 'blocked') return { icon: ArrowLeftRight, className: BLOCKED_ICON_CLASS };
      return state === 'succeeded'
        ? { icon: ArrowLeftRight, className: 'text-emerald-500/60 dark:text-emerald-400/50' }
        : { icon: ArrowLeftRight, className: 'text-rose-500/60 dark:text-rose-400/50' };
    }
    default:
      return { icon: Bot, className: 'opacity-30' };
  }
}

/** Live elapsed timer for in-flight tool calls — ticks every 100ms. */
function LiveDuration({ startTimestamp }: { startTimestamp: number }) {
  const [elapsed, setElapsed] = useState(() => Date.now() - startTimestamp);

  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - startTimestamp), 100);
    return () => clearInterval(id);
  }, [startTimestamp]);

  return (
    <Text as="span" size="xs" color="muted" className="tabular-nums">
      {formatDuration(elapsed)}
    </Text>
  );
}

/** The fixed reason a blocked delegation or node shows, and who can change the setup. */
function BlockedNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 px-2.5 py-2">
      <ShieldAlert className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
      <Text size="xs" className="text-amber-800 dark:text-amber-300">
        {text}
      </Text>
    </div>
  );
}

/** The warning of a step forced to finish, and what to do about it (agion/Agion#2460). */
function ForcedNote({ text }: { text: string }) {
  return (
    <div role="note" className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 px-2.5 py-2">
      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
      <Text size="xs" className="text-amber-800 dark:text-amber-300">
        {text}
      </Text>
    </div>
  );
}

const STATE_BADGE_VARIANT = { succeeded: 'success', failed: 'error', blocked: 'warning' } as const;
const STATE_CONTENT_VARIANT = { succeeded: 'emerald', failed: 'rose', blocked: 'amber' } as const;

/** The badge of a finished delegation: succeeded, failed or blocked by setup. */
function DelegationStateBadge({ state, ...text }: { state: DelegationResultState } & Record<DelegationResultState, string>) {
  return (
    <Badge variant={STATE_BADGE_VARIANT[state]} size="sm">
      {text[state]}
    </Badge>
  );
}

const THINKING_TRUNCATE_LIMIT = 10_000;

/** Thinking content with truncation — large thinking blocks would stall the DOM. */
function ThinkingContent({ content, label, variant }: { content: string; label: string; variant: 'violet' }) {
  const [showFull, setShowFull] = useState(false);
  const needsTruncation = content.length > THINKING_TRUNCATE_LIMIT;
  const displayContent = needsTruncation && !showFull
    ? content.slice(0, THINKING_TRUNCATE_LIMIT) + '\u2026'
    : content;

  return (
    <>
      <ContentBlock content={displayContent} variant={variant} label={label} />
      {needsTruncation && (
        <Button
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); setShowFull((v) => !v); }}
          className="text-xs text-violet-600 dark:text-violet-400 hover:underline mt-1 h-auto p-0"
        >
          {showFull ? 'Show less' : `Show full (${content.length.toLocaleString()} chars)`}
        </Button>
      )}
    </>
  );
}

export interface AuditEntryItemProps {
  entry: AuditEntry;
  isLast: boolean;
  isNew: boolean;
  staggerIndex: number;
  /** Set of tool_call_ids that have received a result — suppresses LiveDuration timer */
  resolvedToolCallIds?: Set<string>;
  /** Override labels for i18n — keys map to translation strings */
  labels?: AuditEntryLabels;
  /** Opens a kept run output on its holding host; without it a kept output has no Open action. */
  onOpenOutput?: OpenKeptOutput;
}

/**
 * Every label key AuditEntryItem reads. Consumers type their label maps with
 * AuditEntryLabels so a renamed key fails their type check instead of
 * silently shipping the English fallback.
 */
export type AuditEntryLabelKey =
  | 'agent' | 'completed' | 'context' | 'delegatedTo' | 'depth' | 'details'
  | 'duration' | 'error' | 'errorDetails' | 'errorMessage' | 'failed'
  | 'guardDeniedDetail' | 'humanInput' | 'humanResponse' | 'in' | 'input'
  | 'model' | 'noConfidentMatch' | 'orchestrator' | 'out' | 'output'
  | 'rankingDegraded' | 'reasoning' | 'response' | 'responseTime' | 'started'
  | 'stopped' | 'succeeded' | 'summary' | 'task' | 'thinking' | 'thinkingRedactedBody'
  | 'tokens' | 'toolResult' | 'turn' | 'userPrompt' | 'waiting' | 'warning'
  | BlockedLabelKey | ForcedLabelKey | KeptOutputLabelKey;

export type AuditEntryLabels = Partial<Record<AuditEntryLabelKey, string>> & Pick<BlockedLabels, 'blockedMessage'>;

const DETAIL_TYPES = new Set([
  'turn', 'tool_call', 'tool_result', 'hitl_request', 'hitl_response',
  'node_complete', 'error', 'orchestrator_decision', 'agent_thinking',
  'delegation', 'delegation_result',
]);

export const AuditEntryItem = memo<AuditEntryItemProps>(function AuditEntryItem({
  entry,
  isLast,
  isNew,
  staggerIndex,
  resolvedToolCallIds,
  labels = {},
  onOpenOutput,
}) {
  const l = (key: AuditEntryLabelKey, fallback: string) => labels[key] ?? fallback;
  const blockedLabel = l('blocked', BLOCKED_LABEL_DEFAULTS.blocked);

  const forcedNote = entry.type === 'node_complete'
    ? forcedEndingNote((entry as NodeCompleteAuditEntry).success, (entry as NodeCompleteAuditEntry).forcedReason, labels)
    : undefined;

  const shouldAutoExpand = entry.type === 'orchestrator_decision'
    || (entry.type === 'node_complete' && !(entry as NodeCompleteAuditEntry).success)
    || forcedNote !== undefined
    || entry.type === 'error';
  const [expanded, setExpanded] = useState(shouldAutoExpand);
  const [mounted, setMounted] = useState(!isNew);

  useEffect(() => {
    if (isNew && !mounted) {
      const timer = setTimeout(() => setMounted(true), staggerIndex * 60);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isNew, mounted, staggerIndex]);

  const { icon: Icon, className: iconClassName } = getEntryIcon(entry);
  const time = formatTime(entry.timestamp);
  const hasDetails = DETAIL_TYPES.has(entry.type);

  const renderTitle = () => {
    switch (entry.type) {
      case 'turn':
        return (
          <Text as="span" size="xs" weight="medium" className="truncate">
            {entry.agentLabel || l('agent', 'Agent')}
          </Text>
        );
      case 'tool_call': {
        const call = entry as ToolCallAuditEntry;
        const isResolved = call.resolved || resolvedToolCallIds?.has(call.toolCallId);
        return (
          <span className="flex items-center gap-1.5">
            <Text as="span" size="xs" className="font-mono truncate text-purple-700 dark:text-purple-300">{call.toolName}</Text>
            {isResolved && call.resolvedDurationMs != null && call.resolvedDurationMs > 0 ? (
              <Text as="span" size="xs" color="muted">{formatDuration(call.resolvedDurationMs)}</Text>
            ) : !isResolved ? (
              <LiveDuration startTimestamp={call.timestamp} />
            ) : null}
          </span>
        );
      }
      case 'tool_result': {
        const result = entry as ToolResultAuditEntry;
        const confidenceState = searchConfidenceState(result.output, result.toolName);
        return (
          <span className="flex items-center gap-1.5 min-w-0">
            <Text as="span" size="xs" className="font-mono truncate">
              {result.toolName || l('toolResult', 'Tool result')}
            </Text>
            {confidenceState === 'degraded' && (
              <Badge variant="warning" size="sm">
                {l('rankingDegraded', 'Ranking degraded')}
              </Badge>
            )}
            {confidenceState === 'none' && (
              <Badge variant="muted" size="sm">
                {l('noConfidentMatch', 'No confident match')}
              </Badge>
            )}
          </span>
        );
      }
      case 'hitl_request': {
        const req = entry as HITLRequestAuditEntry;
        return (
          <span className="flex items-center gap-1.5">
            <Text as="span" size="xs" weight="medium" className="truncate">
              {entry.agentLabel || l('humanInput', 'Human Input')}
            </Text>
            {req.pending && (
              <Badge variant="warning" size="sm">
                {l('waiting', 'waiting...')}
              </Badge>
            )}
          </span>
        );
      }
      case 'hitl_response':
        return (
          <Text as="span" size="xs" weight="medium" className="truncate">
            {entry.userName || l('humanResponse', 'Human Response')}
          </Text>
        );
      case 'node_start': {
        const start = entry as NodeStartAuditEntry;
        return (
          <Text as="span" size="xs">
            <Text as="span" color="muted">{l('started', 'Started')}</Text>{' '}
            <Text as="span" weight="medium">{start.nodeLabel || start.nodeId}</Text>
          </Text>
        );
      }
      case 'node_complete': {
        const complete = entry as NodeCompleteAuditEntry;
        const state = resolveDelegationState(complete.success, complete.outcome);
        return (
          <Text as="span" size="xs">
            {state === 'blocked' ? (
              <Text as="span" className="text-amber-600 dark:text-amber-400">{blockedLabel}</Text>
            ) : (
              <Text as="span" color="muted">
                {isStoppedNode(complete) ? l('stopped', 'Stopped') : state === 'succeeded' ? l('completed', 'Completed') : l('failed', 'Failed')}
              </Text>
            )}{' '}
            <Text as="span" weight="medium">{complete.nodeLabel || complete.nodeId}</Text>
            {forcedNote && (
              <>
                {' '}
                <Badge variant="warning" size="sm">{forcedNote.label}</Badge>
              </>
            )}
          </Text>
        );
      }
      case 'orchestrator_decision':
        return (
          <Text as="span" size="xs" weight="medium" className="truncate">
            {entry.agentLabel || l('orchestrator', 'Orchestrator')}
          </Text>
        );
      case 'agent_thinking':
        return (
          <Text as="span" size="xs" weight="medium" className="truncate">
            {entry.agentLabel || l('thinking', 'Thinking')}
          </Text>
        );
      case 'delegation': {
        const del = entry as DelegationAuditEntry;
        return (
          <span className="flex items-center gap-1.5">
            <Text as="span" size="xs" color="muted">{l('delegatedTo', 'Delegated to')}</Text>
            <Text as="span" size="xs" weight="medium" className="truncate">{del.agentLabel}</Text>
            {del.resolved && (
              <>
                <DelegationStateBadge
                  state={resolveDelegationState(del.resolvedSuccess, del.resolvedOutcome)}
                  succeeded={l('succeeded', 'succeeded')}
                  failed={l('failed', 'failed')}
                  blocked={blockedLabel}
                />
                {del.resolvedDurationMs != null && del.resolvedDurationMs > 0 && (
                  <Text as="span" size="xs" color="muted">{formatDuration(del.resolvedDurationMs)}</Text>
                )}
              </>
            )}
          </span>
        );
      }
      case 'delegation_result': {
        const delResult = entry as DelegationResultAuditEntry;
        return (
          <Text as="span" size="xs" weight="medium" className="truncate">
            {delResult.agentLabel}
          </Text>
        );
      }
      case 'error':
        return isWarningEntry(entry) ? (
          <Text as="span" size="xs" className="text-amber-600 dark:text-amber-400" weight="medium">
            {l('warning', 'Warning')}
          </Text>
        ) : (
          <Text as="span" size="xs" className="text-rose-600 dark:text-rose-400" weight="medium">
            {l('error', 'Error')}
          </Text>
        );
      default:
        return (
          <Text as="span" size="xs" color="muted">{(entry as AuditEntry).type}</Text>
        );
    }
  };

  const renderDetails = () => {
    switch (entry.type) {
      case 'turn': {
        const turn = entry as TurnAuditEntry;
        return (
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <Text as="span" size="xs" color="muted">{l('turn', 'Turn')}</Text>
              <Text as="span" size="xs">{turn.turnNumber + 1}</Text>
            </div>
            {turn.model && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('model', 'Model')}</Text>
                <Text as="span" size="xs" className="font-mono">{turn.model}</Text>
              </div>
            )}
            <div className="flex justify-between">
              <Text as="span" size="xs" color="muted">{l('tokens', 'Tokens')}</Text>
              <Text as="span" size="xs">
                {formatTokens(turn.inputTokens)} {l('in', 'in')} / {formatTokens(turn.outputTokens)} {l('out', 'out')}
                {turn.maxOutputTokens ? ` (max ${formatTokens(turn.maxOutputTokens)})` : ''}
              </Text>
            </div>
            <div className="flex justify-between">
              <Text as="span" size="xs" color="muted">{l('duration', 'Duration')}</Text>
              <Text as="span" size="xs">{formatDuration(turn.durationMs)}</Text>
            </div>
            {turn.userPrompt && (
              <ContentBlock
                content={turn.userPrompt}
                variant="default"
                label={l('userPrompt', 'User Prompt')}
                className="mt-2"
              />
            )}
            {turn.assistantResponse && (
              <ContentBlock
                content={turn.assistantResponse}
                variant="blue"
                label={l('response', 'Response')}
                className="mt-2"
              />
            )}
          </div>
        );
      }
      case 'tool_call': {
        const call = entry as ToolCallAuditEntry;
        const guardDeniedDetail = call.resolvedError || l('guardDeniedDetail', 'Tool not connected to this agent — LLM answered instead.');
        return (
          <div className="space-y-2 text-xs">
            <ContentBlock
              content={call.toolInput}
              isJson
              variant="default"
              label={l('input', 'Input')}
            />
            {call.resolved && (
              <>
                {call.resolvedDeniedByGuard && (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 px-2.5 py-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <Text size="xs" className="text-amber-800 dark:text-amber-300">
                      {guardDeniedDetail}
                    </Text>
                  </div>
                )}
                {call.resolvedError && !call.resolvedDeniedByGuard ? (
                  <ContentBlock content={call.resolvedError} variant="rose" label={l('error', 'Error')} />
                ) : call.resolvedOutput != null ? (
                  <ContentBlock
                    content={call.resolvedOutput as string | Record<string, unknown>}
                    isJson={typeof call.resolvedOutput !== 'string'}
                    variant="default"
                    label={l('output', 'Output')}
                  />
                ) : null}
              </>
            )}
          </div>
        );
      }
      case 'tool_result': {
        const result = entry as ToolResultAuditEntry;
        const formatted = result.output != null ? formatToolOutput(result.output) : null;
        const isOutputJson = result.output != null && typeof result.output !== 'string';
        const guardDeniedDetail = result.error || l('guardDeniedDetail', 'Tool not connected to this agent — LLM answered instead.');
        const confidenceState = searchConfidenceState(result.output, result.toolName);
        const degradedReason = confidenceState === 'degraded' ? searchDegradedReason(result.output, result.toolName) : null;
        return (
          <div className="text-xs space-y-2">
            {result.deniedByGuard && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 px-2.5 py-2">
                <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <Text size="xs" className="text-amber-800 dark:text-amber-300">
                  {guardDeniedDetail}
                </Text>
              </div>
            )}
            {degradedReason && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 px-2.5 py-2">
                <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <Text size="xs" className="text-amber-800 dark:text-amber-300">
                  {degradedReason}
                </Text>
              </div>
            )}
            {result.error && !result.deniedByGuard ? (
              <ContentBlock content={result.error} variant="rose" />
            ) : formatted?.isError ? (
              <ContentBlock content={formatted.summary} variant="rose" />
            ) : formatted ? (
              <div>
                <Text size="xs" color="muted">{formatted.summary}</Text>
                {formatted.hits && <SearchHitsRenderer hits={formatted.hits} />}
              </div>
            ) : result.output ? (
              <ContentBlock
                content={result.output}
                isJson={isOutputJson}
                variant="default"
                label={l('output', 'Output')}
              />
            ) : null}
            {result.durationMs > 0 && (
              <Text size="xs" color="muted">{formatDuration(result.durationMs)}</Text>
            )}
          </div>
        );
      }
      case 'hitl_request': {
        const req = entry as HITLRequestAuditEntry;
        return (
          <div className="text-xs">
            <div className="bg-amber-50 dark:bg-amber-500/10 p-2 rounded-md text-xs text-slate-700 dark:text-slate-300">
              {req.prompt}
            </div>
            {req.options && req.options.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {req.options.map((opt, i) => (
                  <Badge key={i} variant="default" size="sm">{opt}</Badge>
                ))}
              </div>
            )}
          </div>
        );
      }
      case 'hitl_response': {
        const resp = entry as HITLResponseAuditEntry;
        return (
          <div className="text-xs space-y-2">
            <div className="bg-emerald-50 dark:bg-emerald-500/10 p-2 rounded-md text-xs text-slate-700 dark:text-slate-300">
              {resp.response}
            </div>
            {resp.responseTimeMs > 0 && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('responseTime', 'Response time')}</Text>
                <Text as="span" size="xs">{formatHITLDuration(resp.responseTimeMs)}</Text>
              </div>
            )}
          </div>
        );
      }
      case 'node_complete': {
        const complete = entry as NodeCompleteAuditEntry;
        if (complete.success && !forcedNote && complete.output == null && complete.durationMs == null) return null;
        const blocked = resolveDelegationState(complete.success, complete.outcome) === 'blocked';
        return (
          <div className="space-y-2 text-xs">
            {forcedNote && <ForcedNote text={forcedNote.text} />}
            {blocked && (
              <BlockedNote text={blockedReasonText(complete.blockedReason, complete.nodeLabel || complete.nodeId, labels)} />
            )}
            {complete.error && !blocked && (
              <ContentBlock
                content={complete.error}
                variant="rose"
                label={l('errorDetails', 'Error Details')}
              />
            )}
            {complete.output != null && (
              <RunOutput output={complete.output} missionId={complete.missionId} label={l('output', 'Output')}
                labels={labels} onOpen={onOpenOutput} />
            )}
            {complete.durationMs != null && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('duration', 'Duration')}</Text>
                <Text as="span" size="xs">{formatDuration(complete.durationMs)}</Text>
              </div>
            )}
          </div>
        );
      }
      case 'error': {
        const err = entry as ErrorAuditEntry;
        return (
          <div className="space-y-2 text-xs">
            <ContentBlock
              content={err.message}
              variant={isWarningEntry(err) ? 'amber' : 'rose'}
              label={isWarningEntry(err) ? l('details', 'Details') : l('errorMessage', 'Error Message')}
            />
            {err.details != null ? (
              <ContentBlock
                content={err.details as string | Record<string, unknown>}
                isJson={typeof err.details !== 'string'}
                variant="default"
                label={l('details', 'Details')}
              />
            ) : null}
          </div>
        );
      }
      case 'orchestrator_decision': {
        const decision = entry as OrchestratorDecisionAuditEntry;
        return (
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-1.5">
              <Badge variant={
                decision.action === 'delegate' ? 'purple'
                : decision.action === 'complete' ? 'success'
                : decision.action === 'synthesize' ? 'info'
                : 'warning'
              } size="sm">
                {decision.action}
              </Badge>
              {decision.targetAgent && (
                <Text as="span" size="xs" color="muted">{decision.targetAgent}</Text>
              )}
            </div>
            <ContentBlock
              content={decision.reasoning}
              variant="fuchsia"
              label={l('reasoning', 'Reasoning')}
            />
            {decision.model && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('model', 'Model')}</Text>
                <Text as="span" size="xs" className="font-mono">{decision.model}</Text>
              </div>
            )}
            {decision.tokensUsed != null && decision.tokensUsed > 0 && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('tokens', 'Tokens')}</Text>
                <Text as="span" size="xs">{formatTokens(decision.tokensUsed)}</Text>
              </div>
            )}
          </div>
        );
      }
      case 'agent_thinking': {
        const thinking = entry as AgentThinkingAuditEntry;
        if (thinking.redacted) {
          return (
            <div className="text-xs">
              <Text as="span" size="xs" color="muted">
                {l('thinkingRedactedBody', 'Thinking content redacted by model.')}
              </Text>
            </div>
          );
        }
        return (
          <div className="space-y-2 text-xs">
            <ThinkingContent
              content={thinking.content}
              variant="violet"
              label={l('thinking', 'Thinking')}
            />
            {thinking.model && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('model', 'Model')}</Text>
                <Text as="span" size="xs" className="font-mono">{thinking.model}</Text>
              </div>
            )}
          </div>
        );
      }
      case 'delegation': {
        const del = entry as DelegationAuditEntry;
        const state = resolveDelegationState(del.resolvedSuccess, del.resolvedOutcome);
        return (
          <div className="space-y-2 text-xs">
            <ContentBlock
              content={del.task}
              variant="violet"
              label={l('task', 'Task')}
            />
            {del.context && (
              <ContentBlock
                content={del.context}
                variant="default"
                label={l('context', 'Context')}
                className="mt-2"
              />
            )}
            <div className="flex justify-between">
              <Text as="span" size="xs" color="muted">
                {l('depth', 'Depth')}
              </Text>
              <Text as="span" size="xs">{del.depth}</Text>
            </div>
            {del.resolved && (
              <>
                {state === 'blocked' && (
                  <BlockedNote text={blockedReasonText(del.resolvedBlockedReason, del.agentLabel || del.agentNodeId, labels)} />
                )}
                {del.resolvedSummary && (
                  <ContentBlock
                    content={del.resolvedSummary}
                    variant={STATE_CONTENT_VARIANT[state]}
                    label={l('summary', 'Summary')}
                  />
                )}
                {del.resolvedError && state !== 'blocked' && (
                  <ContentBlock content={del.resolvedError} variant="rose" label={l('error', 'Error')} />
                )}
                {del.resolvedTokensUsed != null && del.resolvedTokensUsed > 0 && (
                  <div className="flex justify-between">
                    <Text as="span" size="xs" color="muted">{l('tokens', 'Tokens')}</Text>
                    <Text as="span" size="xs">{formatTokens(del.resolvedTokensUsed)}</Text>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }
      case 'delegation_result': {
        const delResult = entry as DelegationResultAuditEntry;
        const state = resolveDelegationState(delResult.success, delResult.outcome);
        return (
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-1.5">
              <DelegationStateBadge
                state={state}
                succeeded={l('succeeded', 'succeeded')}
                failed={l('failed', 'failed')}
                blocked={blockedLabel}
              />
              {delResult.durationMs > 0 && (
                <Text as="span" size="xs" color="muted">{formatDuration(delResult.durationMs)}</Text>
              )}
            </div>
            {state === 'blocked' && (
              <BlockedNote text={blockedReasonText(delResult.blockedReason, delResult.agentLabel || delResult.agentNodeId, labels)} />
            )}
            {delResult.summary && (
              <ContentBlock
                content={delResult.summary}
                variant={STATE_CONTENT_VARIANT[state]}
                label={l('summary', 'Summary')}
              />
            )}
            {delResult.error && state !== 'blocked' && (
              <ContentBlock content={delResult.error} variant="rose" />
            )}
            {delResult.tokensUsed != null && delResult.tokensUsed > 0 && (
              <div className="flex justify-between">
                <Text as="span" size="xs" color="muted">{l('tokens', 'Tokens')}</Text>
                <Text as="span" size="xs">{formatTokens(delResult.tokensUsed)}</Text>
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div
      id={`audit-entry-${entry.id}`}
      className={cn(
        'relative group transition-all duration-300 ease-out',
        isNew && !mounted && 'opacity-0 translate-y-2',
        isNew && mounted && 'opacity-100 translate-y-0',
      )}
    >
      {/* Timeline connector line */}
      {!isLast && (
        <div className="absolute left-3 top-7 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />
      )}

      <div
        className={cn(
          'flex gap-3 py-1.5 rounded-md transition-colors',
          hasDetails && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50',
          expanded && 'bg-slate-50 dark:bg-slate-800/30'
        )}
        onClick={() => hasDetails && setExpanded(!expanded)}
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        onKeyDown={hasDetails ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } } : undefined}
      >
        {/* Icon — items-start keeps icon aligned with title when details expand */}
        <div className="flex-shrink-0 w-6 flex items-start justify-center pt-0.5">
          <Icon className={cn(
            'h-3.5 w-3.5 transition-transform',
            iconClassName,
            isNew && mounted && 'animate-[icon-pop_0.4s_ease-out]',
          )} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 min-w-0">
              <div className="truncate">{renderTitle()}</div>
              {hasDetails && (
                expanded
                  ? <ChevronDown className="h-3 w-3 opacity-30 flex-shrink-0" />
                  : <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-30 transition-opacity flex-shrink-0" />
              )}
            </div>
            <Text as="span" size="xs" color="subtle" className="flex-shrink-0">{time}</Text>
          </div>

          {/* Smooth expand/collapse via grid row transition */}
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-out',
              expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="overflow-hidden">
              {hasDetails && (
                <div className="mt-2 mb-1">
                  {renderDetails()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
